import {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  Suspense,
  useCallback,
} from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { $generateNodesFromDOM } from "@lexical/html";
import { $getRoot } from "lexical";
import { ListItemNode, ListNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { CodeNode, CodeHighlightNode } from "@lexical/code";
import PlaygroundEditorTheme from "./LexicalEditor/themes/PlaygroundEditorTheme";
import PlaygroundNodes from "./LexicalEditor/nodes/PlaygroundNodes";
import { SharedHistoryContext } from "./LexicalEditor/context/SharedHistoryContext";
import { TableContext } from "./LexicalEditor/plugins/TablePlugin";
import { ToolbarContext } from "./LexicalEditor/context/ToolbarContext";
import { SettingsContext } from "./LexicalEditor/context/SettingsContext";
import Editor from "./Editor/Editor";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { buildImportMap, cleanBaselineHtml } from "../../util/lexical";
import "./Editor/index.css";

// Plugin to handle read-only state
const ReadOnlyPlugin = ({ disabled }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Set the editor's editable state based on the disabled prop
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  return null;
};

/**
 * ShortDescriptionEditor - A Lexical rich text editor for short descriptions
 * Uses the exact same structure as RichTextForm to ensure consistent UI/UX
 */
const ShortDescriptionEditor = (props) => {
  const {
    value,
    onChange,
    onDirtyChange,
    placeholder,
    minHeight,
    disabled,
    isModulePreview,
    compactLayout,
    /** When true with disabled: hide toolbar but keep Lexical content (e.g. Add Module live mode). */
    hideReadOnlyToolbar = false,
    /** Optional sticky top override in px; use 0 to pin toolbar to container top edge. */
    stickyToolbarTopOverridePx,
  } = props;
  const placeholderText = placeholder || "Enter short description";
  const editorMinHeight = minHeight || "100px";
  const isPreviewReadOnly = Boolean(disabled && isModulePreview);
  const hasStickyTopOverride = Number.isFinite(stickyToolbarTopOverridePx);
  const resolvedInitialStickyTop = hasStickyTopOverride
    ? Number(stickyToolbarTopOverridePx)
    : compactLayout
      ? 107
      : 95;
  const [isInitialized, setIsInitialized] = useState(false);
  /** Sticky offset for toolbar = actual AppBar height (avoids gap from hardcoded 95/107px). */
  const [toolbarStickyTopPx, setToolbarStickyTopPx] = useState(
    () => resolvedInitialStickyTop,
  );
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);
  const appBarRef = useRef(null);

  // Prevent Lexical dropdown from hiding page scrollbar
  useEffect(() => {
    if (disabled) return;

    const originalBodyOverflow = document.body.style.overflow;

    const handleDropdownOpen = () => {
      // Don't allow Lexical to hide body overflow when our editor is active
      if (originalBodyOverflow !== "hidden") {
        // Store original if not already hidden
        if (!window.lexicalBodyOverflowStored) {
          window.lexicalBodyOverflowStored = originalBodyOverflow;
        }
        // Only set hidden if not already hidden by something else
        document.body.style.overflow = originalBodyOverflow || "auto";
      }
    };

    const handleDropdownClose = () => {
      // Restore original overflow when all dropdowns close
      setTimeout(() => {
        const openDropdowns = document.querySelectorAll(".dropdown.open");
        if (
          openDropdowns.length === 0 &&
          window.lexicalBodyOverflowStored !== undefined
        ) {
          document.body.style.overflow = window.lexicalBodyOverflowStored;
          delete window.lexicalBodyOverflowStored;
        }
      }, 0);
    };

    // Monitor dropdown state changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.classList && node.classList.contains("dropdown")) {
            handleDropdownOpen();
          }
        });
        mutation.removedNodes.forEach((node) => {
          if (node.classList && node.classList.contains("dropdown")) {
            handleDropdownClose();
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      // Cleanup on unmount
      if (window.lexicalBodyOverflowStored !== undefined) {
        document.body.style.overflow = window.lexicalBodyOverflowStored;
        delete window.lexicalBodyOverflowStored;
      }
    };
  }, [disabled]);

  // Handle editor changes with HTML cleaning
  const handleEditorChange = useCallback(
    (content) => {
      // Don't allow changes when disabled
      if (disabled) return;

      const cleanedContent = cleanBaselineHtml(content);
      onChange(cleanedContent);
    },
    [onChange, disabled],
  );

  // Set initial HTML content
  const setInitialHtmlContent = (htmlString) => {
    if (!htmlString) return undefined;

    // Clean the HTML string - remove editor wrapper, then trim only leading/trailing
    // empty paragraphs (Lexical artifacts). Empty paragraphs in the middle are
    // intentional blank lines added by the user and must be preserved.
    let cleanedHtml = htmlString
      .replace(/<div class="editor-shell">/g, "")
      .replace(/<\/div>\s*$/, "");

    const emptyParaPattern = /^\s*(<p[^>]*>(\s|<br\s*\/?>|&nbsp;)*<\/p>\s*)+/i;
    // Strip leading empty paragraphs
    cleanedHtml = cleanedHtml.replace(emptyParaPattern, "");
    // Strip trailing empty paragraphs
    cleanedHtml = cleanedHtml.replace(
      /(\s*<p[^>]*>(\s|<br\s*\/?>|&nbsp;)*<\/p>)+\s*$/gi,
      "",
    );

    // Create editor state from HTML
    const initialEditorState = (editor) => {
      // Parse HTML string into DOM
      const parser = new DOMParser();
      const dom = parser.parseFromString(cleanedHtml, "text/html");

      editor.update(() => {
        // Generate nodes from DOM
        const nodes = $generateNodesFromDOM(editor, dom, {
          preserveWhitespace: true,
        });
        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
    };

    return initialEditorState;
  };

  const initialConfig = {
    editorState: value ? setInitialHtmlContent(value) : undefined,
    html: { import: buildImportMap() },
    namespace: "ShortDescriptionEditor",
    nodes: [...PlaygroundNodes],
    onError: (error) => {
      console.error("Lexical error:", error);
    },
    theme: PlaygroundEditorTheme,
  };

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Measure AppBar height so sticky toolbar `top` matches the real header (ModuleForm uses compactLayout).
  useLayoutEffect(() => {
    if (isModulePreview || disabled || hasStickyTopOverride) return;

    const measure = () => {
      const appBar = appBarRef.current || document.querySelector(".appbar");
      if (!appBar) return;
      const h = Math.round(appBar.getBoundingClientRect().height);
      if (h > 0) {
        setToolbarStickyTopPx((prev) => (prev === h ? prev : h));
      }
    };

    measure();
    const appBar = appBarRef.current || document.querySelector(".appbar");
    let ro = null;
    if (appBar && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      ro.observe(appBar);
    }
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [isModulePreview, disabled, hasStickyTopOverride]);

  useEffect(() => {
    if (!hasStickyTopOverride) return;
    const nextTop = Number(stickyToolbarTopOverridePx);
    setToolbarStickyTopPx((prev) => (prev === nextTop ? prev : nextTop));
  }, [hasStickyTopOverride, stickyToolbarTopOverridePx]);

  // When switching from read-only/live mode back to edit mode, clear any stale
  // sticky offset and allow fresh AppBar measurement to set the final value.
  useEffect(() => {
    if (disabled || hasStickyTopOverride) return;
    const baselineTop = compactLayout ? 107 : 95;
    setToolbarStickyTopPx((prev) => (prev === baselineTop ? prev : baselineTop));
  }, [disabled, compactLayout, hasStickyTopOverride]);

  // Keep AppBar and toolbar sticky (same pattern as RichTextForm)
  useEffect(() => {
    if (isModulePreview || disabled) return;

    let rafId = null;
    let checkTimeout = null;
    let isChecking = false;
    let scrollHandler = null;

    const ensureStickyPositioning = () => {
      if (isChecking) return;
      isChecking = true;
      try {
        let topPx = hasStickyTopOverride
          ? Number(stickyToolbarTopOverridePx)
          : compactLayout
            ? 107
            : 95;
        if (!hasStickyTopOverride) {
          const appBar = appBarRef.current || document.querySelector(".appbar");
          if (appBar) {
            appBarRef.current = appBar;
            appBar.style.setProperty("position", "sticky", "important");
            appBar.style.setProperty("top", "0", "important");
            appBar.style.setProperty("z-index", "1000", "important");
            const h = Math.round(appBar.getBoundingClientRect().height);
            if (h > 0) topPx = h;
          }
        }

        // Find all short-description toolbars and enforce sticky styles
        const toolbars = document.querySelectorAll(
          ".short-description-editor-wrapper .toolbars",
        );
        toolbars.forEach((toolbar) => {
          toolbar.style.setProperty("position", "sticky", "important");
          toolbar.style.setProperty("top", `${topPx}px`, "important");
          toolbar.style.setProperty("z-index", "100", "important");
          toolbar.style.setProperty("background", "#f9f9f9", "important");
          toolbar.style.setProperty("margin", "0", "important");
          toolbar.style.setProperty("padding", "2px 8px", "important");
        });
      } finally {
        isChecking = false;
      }
    };

    const scheduleCheck = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        ensureStickyPositioning();
      });
    };

    const debouncedEnsureSticky = () => {
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }
      checkTimeout = setTimeout(scheduleCheck, 50);
    };

    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      mutations.forEach((mutation) => {
        const target = mutation.target;
        if (
          target === appBarRef.current ||
          target === toolbarRef.current ||
          (appBarRef.current && target.contains?.(appBarRef.current)) ||
          (toolbarRef.current && target.contains?.(toolbarRef.current)) ||
          (mutation.type === "attributes" &&
            (mutation.attributeName === "style" ||
              mutation.attributeName === "class"))
        ) {
          shouldCheck = true;
        }
      });
      if (shouldCheck) {
        debouncedEnsureSticky();
      }
    });

    const observeElements = () => {
      const appBar = appBarRef.current || document.querySelector(".appbar");
      const wrapper =
        toolbarRef.current ||
        document.querySelector(".short-description-editor-wrapper");
      const toolbars = document.querySelectorAll(
        ".short-description-editor-wrapper .toolbars",
      );

      if (appBar) {
        observer.observe(appBar, {
          attributes: true,
          attributeFilter: ["style", "class"],
        });
        if (appBar.parentElement) {
          observer.observe(appBar.parentElement, {
            attributes: true,
            attributeFilter: ["style", "class"],
          });
        }
      }
      if (wrapper) {
        observer.observe(wrapper, {
          attributes: true,
          attributeFilter: ["style", "class"],
          childList: true,
        });
      }
      toolbars.forEach((toolbar) => {
        observer.observe(toolbar, {
          attributes: true,
          attributeFilter: ["style", "class"],
        });
      });
    };

    ensureStickyPositioning();
    setTimeout(ensureStickyPositioning, 100);
    setTimeout(ensureStickyPositioning, 300);
    setTimeout(ensureStickyPositioning, 1000);
    setTimeout(observeElements, 100);
    setTimeout(observeElements, 500);

    scrollHandler = () => scheduleCheck();
    window.addEventListener("scroll", scrollHandler, { passive: true });
    window.addEventListener("resize", scrollHandler, { passive: true });
    const intervalId = setInterval(ensureStickyPositioning, 2000);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (checkTimeout) clearTimeout(checkTimeout);
      observer.disconnect();
      clearInterval(intervalId);
      if (scrollHandler) {
        window.removeEventListener("scroll", scrollHandler);
        window.removeEventListener("resize", scrollHandler);
      }
    };
  }, [
    isModulePreview,
    compactLayout,
    disabled,
    hasStickyTopOverride,
    stickyToolbarTopOverridePx,
  ]);

  if (!isInitialized) {
    return (
      <div
        style={{
          minHeight: editorMinHeight,
          border: "1px solid #ddd",
          borderRadius: "4px",
          padding: "8px",
        }}
      >
        Loading editor...
      </div>
    );
  }

  return (
    <SettingsContext>
      <SharedHistoryContext>
        <div
          className={`short-description-editor-wrapper${
            disabled && hideReadOnlyToolbar && !isModulePreview
              ? " short-description-editor-wrapper--read-only-no-toolbar"
              : ""
          }`}
          ref={toolbarRef}
        >
          <style>
            {`
            .short-description-editor-wrapper {
              border: 1px solid #ddd;
              border-radius: 4px;
              background: white;
              overflow-x: clip;
              overflow-y: visible;
              margin: 0;
              padding: 0;
              position: relative;
            }
            .short-description-editor-wrapper .editor-shell {
              min-height: ${editorMinHeight};
              overflow-x: clip;
              display: flex;
              flex-direction: column;
              position: relative;
              width: 100%;
              max-width: 100%;
              min-width: 0;
              box-sizing: border-box;
            }
            .short-description-editor-wrapper--read-only-no-toolbar .editor-shell {
              min-height: 0;
            }
            .short-description-editor-wrapper--read-only-no-toolbar .editor-containerss {
              margin-top: 0 !important;
              padding-top: 0 !important;
            }
            .short-description-editor-wrapper--read-only-no-toolbar .editor-scroller {
              min-height: ${editorMinHeight};
            }
            .short-description-editor-wrapper--read-only-no-toolbar
              .editor-shell
              .editor-containerss {
              border-top-left-radius: 10px;
              border-top-right-radius: 10px;
            }
            .short-description-editor-wrapper .toolbars {
              border-bottom: 1px solid #ddd;
              padding: 2px 8px;
              margin: 0;
              background: ${disabled && !isPreviewReadOnly ? "#e9ecef" : "#f9f9f9"};
              pointer-events: ${disabled ? "none" : "auto"};
              opacity: ${disabled && !isPreviewReadOnly ? 0.6 : 1};
              position: ${isModulePreview ? "static" : "sticky"} !important;
              top: ${isModulePreview ? "auto" : `${toolbarStickyTopPx}px`} !important;
              z-index: ${isModulePreview ? "auto" : "100"} !important;
              box-shadow: none;
              width: 100%;
              max-width: 100%;
              min-width: 0;
              box-sizing: border-box;
              overflow-x: hidden;
              overflow-y: hidden;
              flex-wrap: wrap;
              min-height: 36px;
              height: auto;
              align-content: center;
              gap: 2px 0;
            }
            /* Preview route: show toolbar but disable every control */
            ${
              isModulePreview
                ? `
            .short-description-editor-wrapper .toolbars button,
            .short-description-editor-wrapper .toolbars [role="button"],
            .short-description-editor-wrapper .toolbars select,
            .short-description-editor-wrapper .toolbars input {
              pointer-events: none !important;
              cursor: not-allowed !important;
            }
            `
                : ""
            }
            /* Responsive toolbar: smaller icons and padding so toolbar fits without scrollbar */
            @media (max-width: 1400px) {
              .short-description-editor-wrapper .toolbars .toolbar-item {
                padding: 4px 6px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item i.format {
                height: 14px !important;
                width: 14px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item .icon {
                width: 16px !important;
                height: 16px !important;
                margin-right: 4px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item .text {
                font-size: 12px !important;
                height: 18px !important;
                line-height: 18px !important;
                padding-right: 6px !important;
              }
              .short-description-editor-wrapper .toolbars i.chevron-down,
              .short-description-editor-wrapper .toolbars .toolbar-item i.chevron-down {
                width: 12px !important;
                height: 12px !important;
              }
              .short-description-editor-wrapper .toolbars .divider {
                min-height: 24px !important;
                margin: 0 4px !important;
              }
              .short-description-editor-wrapper .toolbars .block-controls .dropdown-button-text {
                max-width: 5em !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item.font-family .text {
                max-width: 32px !important;
              }
              .short-description-editor-wrapper .toolbars .code-language {
                width: 120px !important;
              }
            }
            @media (max-width: 1200px) {
              .short-description-editor-wrapper .toolbars {
                padding: 2px 4px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item {
                padding: 2px 4px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item i.format {
                height: 12px !important;
                width: 12px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item .icon {
                width: 14px !important;
                height: 14px !important;
                margin-right: 2px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item .text {
                font-size: 11px !important;
                height: 16px !important;
                line-height: 16px !important;
                padding-right: 4px !important;
              }
              .short-description-editor-wrapper .toolbars i.chevron-down,
              .short-description-editor-wrapper .toolbars .toolbar-item i.chevron-down {
                width: 10px !important;
                height: 10px !important;
              }
              .short-description-editor-wrapper .toolbars .divider {
                min-height: 20px !important;
                margin: 0 2px !important;
              }
              .short-description-editor-wrapper .toolbars .block-controls .dropdown-button-text {
                max-width: 4em !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item.font-family .text {
                max-width: 28px !important;
              }
              .short-description-editor-wrapper .toolbars .code-language {
                width: 100px !important;
              }
            }
            @media (max-width: 992px) {
              .short-description-editor-wrapper .toolbars {
                min-height: 32px !important;
                padding: 2px 4px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item {
                padding: 2px 3px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item i.format {
                height: 11px !important;
                width: 11px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item .icon {
                width: 12px !important;
                height: 12px !important;
                margin-right: 2px !important;
              }
              .short-description-editor-wrapper .toolbars .toolbar-item .text {
                font-size: 10px !important;
                height: 14px !important;
                line-height: 14px !important;
                padding-right: 2px !important;
              }
              .short-description-editor-wrapper .toolbars .divider {
                min-height: 18px !important;
                margin: 0 2px !important;
              }
              .short-description-editor-wrapper .toolbars .block-controls .dropdown-button-text {
                max-width: 3.5em !important;
              }
              .short-description-editor-wrapper .toolbars .code-language {
                width: 85px !important;
              }
            }
            /* Prevent margins on truly empty paragraphs only */
            .short-description-editor-wrapper p:empty {
              margin: 0 !important;
              padding: 0 !important;
              min-height: 0 !important;
            }
          `}
          </style>
          <Suspense fallback={<div>Loading editor...</div>}>
            <LexicalComposer initialConfig={initialConfig}>
              <ReadOnlyPlugin disabled={disabled} />
              <TableContext>
                <ToolbarContext>
                  <div className="editor-shell" ref={editorRef}>
                    <Editor
                      handleEditorChange={handleEditorChange}
                      initialContent={value || ""}
                      placeholder={placeholderText}
                      onDirtyChange={onDirtyChange}
                      showToolbarWhenReadOnly={Boolean(
                        isModulePreview ||
                          (disabled && !hideReadOnlyToolbar),
                      )}
                    />
                  </div>
                </ToolbarContext>
              </TableContext>
            </LexicalComposer>
          </Suspense>
        </div>
      </SharedHistoryContext>
    </SettingsContext>
  );
};

export default ShortDescriptionEditor;
